import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { MessagesOrder } from 'src/sb-tools/typescript/enums/messages-order.enum';
import { MessageThreadElementType } from '@sb-tools/typescript/enums/message-thread-element-type.enum';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { FilterCollapseOptionsModel } from '../../core/models/filter-collapse-options.model';
import { ThreadFilterGuiModel } from '../../core/models/thread-filter-gui.model';
import { ThreadFilterService } from '../../core/services/thread-filter.service';
import { ReplyService } from '../../core/services/ticket/reply.service';
import { ThreadFilterType } from '../../core/enums/thread-filter-type.enum';

const SEARCH_QUERY_MIN_LENGTH = 2;

@Injectable({ providedIn: 'root' })
@Pipe({ name: 'filterReplies' })
export class FilterRepliesPipe implements PipeTransform {
    private hasFilterOrSearchQuery: boolean;
    private searchQueryLower;
    private threadFilterService = ThreadFilterService;

    constructor(
        private replyService: ReplyService,
        private commonDataService: CommonDataService,
    ) {}

    transform(
        replies: ThreadElementDTO[], // replies for filtering
        collapseOptions: FilterCollapseOptionsModel, // includes: take(sum of shown replies) and threadCollapsed options
        replySortBy: MessagesOrder,
        searchQuery?: string,
        filter?: ThreadFilterGuiModel,
        replyCount?: number, // need to refilter when it was changed
        updateStamp?: number, // need to refilter when it was changed
        defaultCount = 2, // count of replies that should be shown without collapsing
        isPrinting = false,
        selectedReply?: ThreadElementDTO,
    ): any {
        let isNeedToCollapse = true;

        this.resetTransparency(replies);

        if (selectedReply && !this.replyService.wasReplyScrolledToView) {
            return this.expandSelectedReply(replies, selectedReply, collapseOptions, defaultCount);
        }

        this.hasFilterOrSearchQuery = !!(searchQuery?.length > SEARCH_QUERY_MIN_LENGTH || filter);

        this.threadFilterService.updateFilterSet(this.hasFilterOrSearchQuery);

        if (this.hasFilterOrSearchQuery) {
            isNeedToCollapse = false;

            if (searchQuery?.length > SEARCH_QUERY_MIN_LENGTH) {
                this.filterBySearchQuery(replies, searchQuery);
            } else {
                this.filterByThreadFilter(filter, replies);
            }
        }

        switch (replySortBy) {
            case MessagesOrder.creationTimeDesc: {
                replies = [...replies].sort((a, b) => {
                    const timeA = new Date(a.threadLastUpdateTime || a.creationTime).getTime();
                    const timeB = new Date(b.threadLastUpdateTime || b.creationTime).getTime();
                    return timeB - timeA;
                });

                break;
            }

            case MessagesOrder.creationTimeAsc:
            default: {
                replies = [...replies].sort((a, b) => {
                    const timeA = new Date(a.threadLastUpdateTime || a.creationTime).getTime();
                    const timeB = new Date(b.threadLastUpdateTime || b.creationTime).getTime();
                    return timeA - timeB;
                });

                break;
            }
        }

        if (isNeedToCollapse) {
            this.collapseReplies(replies, collapseOptions, defaultCount, replySortBy, isPrinting);
        }

        ThreadFilterService.hasVisibleReplies = !!replies.find((reply) => {
            return !reply.isRemovedByFilter || reply.isTransparent;
        });

        return replies;
    }

    private collapseReplies(replies: ThreadElementDTO[], collapseOptions, defaultCount, replySortBy, isPrinting): void {
        // Session authentication events are timeline markers, not replies, so they must not affect collapse counts.
        const threadItemsToCollapse = replies.filter((reply) => !this.isSessionAuthenticationEvent(reply));
        const hiddenItemsCount = threadItemsToCollapse.length - defaultCount;
        let extraItemsToShow = collapseOptions.takeReplies;

        if (extraItemsToShow > hiddenItemsCount) {
            extraItemsToShow = hiddenItemsCount;
        }

        if ((!collapseOptions.isCollapsed && extraItemsToShow < hiddenItemsCount) || isPrinting) {
            extraItemsToShow = hiddenItemsCount;
        }

        if (threadItemsToCollapse.length < defaultCount) {
            return;
        }

        // Depending on sort order, hide items outside the visible window while keeping auth markers untouched.
        switch (replySortBy) {
            case MessagesOrder.creationTimeDesc: {
                for (let i = extraItemsToShow + defaultCount; i < threadItemsToCollapse.length; ++i) {
                    threadItemsToCollapse[i].isRemovedByFilter = true;
                }

                break;
            }

            case MessagesOrder.creationTimeAsc:
            default: {
                const hiddenItemsBeforeVisibleWindow = threadItemsToCollapse.length - extraItemsToShow - defaultCount;

                for (let i = 0; i < hiddenItemsBeforeVisibleWindow; ++i) {
                    threadItemsToCollapse[i].isRemovedByFilter = true;
                }

                break;
            }
        }
    }

    private containsBadWords(reply: ThreadElementDTO): boolean {
        return reply.containsBadWords;
    }

    private containsEssence(reply: ThreadElementDTO): boolean {
        let message = reply.message.text;
        message = message.replace(
            /@\[([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}]/gi,
            '',
        ); // regex to replace userId
        message = message.replace(
            /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
            '',
        ); // regex to replace emojis
        message = message.replace(/\s\s+/g, ' ');

        if (message.length < 50) {
            return false;
        }

        return true;
    }

    private containsQuestions(reply: ThreadElementDTO): boolean {
        return reply.message.text.indexOf('?') !== -1;
    }

    private containsText(reply: ThreadElementDTO, searchQueryLower: string): boolean {
        const whitespaceRegex = new RegExp(String.fromCharCode(160), 'g'); // regexp to replace &nbsp;

        return (
            reply.message.text
                .replace(whitespaceRegex, ' ')
                .toLowerCase()
                .indexOf(searchQueryLower.replace(whitespaceRegex, ' ')) !== -1 ||
            (reply.author.name || reply.author.email)
                .replace(whitespaceRegex, ' ')
                .toLowerCase()
                .indexOf(searchQueryLower) !== -1
        );
    }

    private expandSelectedReply(replies, selectedReply, collapseOptions, defaultCount: number) {
        if (replies.length >= defaultCount) {
            let selectedReplyIndex;
            if (selectedReply.parentReplyIds.length) {
                const parentReplyId = selectedReply.parentReplyIds[0];

                selectedReplyIndex = replies.findIndex((reply) => {
                    return reply.id === parentReplyId;
                });

                // expand parents of nested reply (usually twitter ticket)
                if (selectedReply.parentReplyIds.length > 1) {
                    selectedReply.parentReplyIds.forEach((parentId) => {
                        const parentReply = this.replyService.findReply(replies, parentId);

                        if (parentReply) {
                            parentReply.areCommentsShown = true;
                        }
                    });
                }
            } else {
                selectedReplyIndex = replies.findIndex((reply) => {
                    return reply.id === selectedReply.id;
                });
            }

            if (selectedReplyIndex > -1) {
                let startIndexOfRest = Math.ceil(selectedReplyIndex / 50) * 50 || 50;

                if (replies.length >= 52) {
                    collapseOptions.takeReplies = startIndexOfRest;
                    startIndexOfRest = startIndexOfRest + 3;
                } else {
                    collapseOptions.takeReplies = startIndexOfRest + 1;
                    startIndexOfRest = startIndexOfRest + 2;
                }

                if (replies.length <= startIndexOfRest - 1) {
                    collapseOptions.isCollapsed = false;
                }
                this.replyService.wasReplyScrolledToView = true;

                return replies.slice(0, startIndexOfRest);
            }
        } else {
            this.replyService.wasReplyScrolledToView = true;
            return replies;
        }
    }

    private filter(
        replies: ThreadElementDTO[],
        isValid: (reply: ThreadElementDTO, searchQueryLower: string) => boolean,
    ): void {
        replies.forEach((reply) => {
            if (this.isSessionAuthenticationEvent(reply)) {
                reply.isRemovedByFilter = false;
                return;
            }

            reply.isRemovedByFilter = !isValid(reply, this.searchQueryLower);
            this.filter(this.getSubReplies(reply), isValid);
        });
    }

    private filterBySearchQuery(replies: ThreadElementDTO[], searchQuery: string): void {
        this.searchQueryLower = searchQuery.toLowerCase();

        this.markTransparent(replies, this.containsText);
        this.filter(replies, this.containsText);
    }

    private filterByThreadFilter(filter: ThreadFilterGuiModel, replies: ThreadElementDTO[]): void {
        switch (filter.type) {
            case ThreadFilterType.badWords: {
                this.markTransparent(replies, this.containsBadWords);
                this.filter(replies, this.containsBadWords);
                break;
            }
            case ThreadFilterType.unhandled: {
                this.markTransparent(replies, this.unhandledReplies);
                this.filter(replies, this.unhandledReplies);
                break;
            }
            case ThreadFilterType.handledByMe: {
                this.markTransparent(replies, this.handledByMeReplies.bind(this));
                this.filter(replies, this.handledByMeReplies.bind(this));
                break;
            }
            case ThreadFilterType.handled: {
                this.markTransparent(replies, this.handledReplies);
                this.filter(replies, this.handledReplies);
                break;
            }
            case ThreadFilterType.questions: {
                this.markTransparent(replies, this.containsQuestions);
                this.filter(replies, this.containsQuestions);
                break;
            }
            case ThreadFilterType.essence: {
                this.markTransparent(replies, this.containsEssence);
                this.filter(replies, this.containsEssence);
                break;
            }
            case ThreadFilterType.unansweredThreads: {
                replies.forEach((value) => {
                    if (this.isSessionAuthenticationEvent(value)) {
                        value.isRemovedByFilter = false;
                    } else if (this.getSubReplies(value).length > 0) {
                        const subReplies = this.getSubReplies(value);
                        const comment = subReplies[subReplies.length - 1];
                        if (comment.isByTeam) {
                            value.isRemovedByFilter = true;
                        }
                    } else {
                        value.isRemovedByFilter = true;
                    }
                });
                break;
            }
        }
    }

    private findValidReply(
        replies: ThreadElementDTO[],
        isValid: (reply: ThreadElementDTO, searchQueryLower: string) => boolean,
    ): ThreadElementDTO {
        if (!replies?.length) {
            return null;
        }

        const reply = replies.find((message) => {
            return message.type === MessageThreadElementType.reply && isValid(message, this.searchQueryLower);
        });

        if (!reply) {
            let subReplies: ThreadElementDTO[] = [];

            replies.forEach((message) => {
                subReplies = subReplies.concat(this.getSubReplies(message));
            });

            return this.findValidReply(subReplies, isValid);
        } else {
            return reply;
        }
    }

    private handledByMeReplies(reply: ThreadElementDTO): boolean {
        return reply.handledBy?.id === this.commonDataService.user.id;
    }

    private handledReplies(reply: ThreadElementDTO): boolean {
        return !!reply.handledBy?.id;
    }

    private markTransparent(
        replies: ThreadElementDTO[],
        isValid: (reply: ThreadElementDTO, searchQueryLower: string) => boolean,
    ) {
        let subReplies: ThreadElementDTO[] = [];

        replies.forEach((reply: ThreadElementDTO) => {
            if (this.isSessionAuthenticationEvent(reply)) {
                reply.isTransparent = false;
                reply.isRemovedByFilter = false;
                return;
            }

            const replySubReplies = this.getSubReplies(reply);
            const hasValidSubReplies = this.findValidReply(replySubReplies, isValid);
            if (hasValidSubReplies && !isValid(reply, this.searchQueryLower)) {
                reply.isTransparent = true;
            } else {
                reply.isTransparent = false;
            }

            if (hasValidSubReplies) {
                reply.areCommentsShown = true;
            }

            subReplies = subReplies.concat(replySubReplies);
        });

        if (subReplies.length) {
            return this.markTransparent(subReplies, isValid);
        } else {
            return null;
        }
    }

    private resetTransparency(replies: ThreadElementDTO[]) {
        let subComments = [];

        replies.forEach((comment) => {
            comment.isTransparent = false;
            comment.isRemovedByFilter = false;
            subComments = subComments.concat(this.getSubReplies(comment));

            if (this.threadFilterService.isFilterSet) {
                comment.areCommentsShown = false;
            }
        });

        if (subComments.length) {
            return this.resetTransparency(subComments);
        } else {
            return false;
        }
    }

    private unhandledReplies(reply: ThreadElementDTO): boolean {
        return !reply.handledBy && !reply.isByTeam && !reply.isDeleted;
    }

    private getSubReplies(reply: ThreadElementDTO): ThreadElementDTO[] {
        return reply.subReplies || [];
    }

    private isSessionAuthenticationEvent(reply: ThreadElementDTO): boolean {
        return reply.type === MessageThreadElementType.sessionAuthentication;
    }
}
